import { z } from 'zod';

/**
 * Push notifications to the technician app (2026-09-16).
 *
 * The phone registers the Expo push token its handset was given, and the
 * platform sends to it through Expo's push service — for two things only: a
 * deployment job assigned to that person, and a PON-wide light drop in a zone
 * they hold an open job in. Nothing here is a second notification store: the
 * bell (`services/notifications.ts`) stays a view over open findings, and a
 * push is a ping that something already recorded has happened.
 *
 * The `data` payload is the one contract both ends share: what the phone
 * deep-links to when the notification is tapped. Kept as a discriminated union
 * so a client can refuse anything that is not one of the two shapes rather
 * than routing on a stray string.
 */

export const PUSH_PLATFORMS = ['ios', 'android'] as const;
export const pushPlatformSchema = z.enum(PUSH_PLATFORMS);
export type PushPlatform = z.infer<typeof pushPlatformSchema>;

/**
 * What an Expo push token looks like — `ExponentPushToken[…]` on every current
 * SDK, `ExpoPushToken[…]` on older ones. Checked at the door so a device
 * token from FCM or APNs handed over by mistake is refused by name rather than
 * stored and sent to Expo, which would answer `InvalidCredentials` per message.
 */
export const EXPO_PUSH_TOKEN_PATTERN = /^Expo(?:nent)?PushToken\[[A-Za-z0-9_-]{8,200}\]$/;

export function isExpoPushToken(value: string): boolean {
  return EXPO_PUSH_TOKEN_PATTERN.test(value);
}

/** `PUT /api/push/token` */
export const registerPushTokenSchema = z.object({
  token: z
    .string()
    .regex(EXPO_PUSH_TOKEN_PATTERN, 'Send the Expo push token the handset was given (ExponentPushToken[…]).'),
  platform: pushPlatformSchema,
  /** The handset's own name, for the person's device list — never required. */
  deviceName: z.string().trim().max(120).optional(),
});
export type RegisterPushToken = z.infer<typeof registerPushTokenSchema>;

/** `DELETE /api/push/token` */
export const deletePushTokenSchema = z.object({
  token: z.string().regex(EXPO_PUSH_TOKEN_PATTERN, 'Send the Expo push token to remove.'),
});
export type DeletePushToken = z.infer<typeof deletePushTokenSchema>;

/**
 * The `data` every notification carries — where a tap goes.
 *
 * `job` opens the job screen; `onu` opens the ONU screen (with the job that
 * put the person in that zone beside it, so a client that prefers the job
 * can take it). Ids are the platform's row ids, the same ones the app's
 * routes already take.
 */
export const pushNotificationDataSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('job'), jobId: z.string().min(1) }),
  z.object({ kind: z.literal('onu'), onuId: z.string().min(1), jobId: z.string().min(1).optional() }),
]);
export type PushNotificationData = z.infer<typeof pushNotificationDataSchema>;
